/**
 * HACCP — page orchestrator. Loads the active site's CCP register and any
 * open corrective actions, then renders them via the tabbed sub-components
 * <CCPRegisterTab /> and <CorrectiveActionsTab />. The page itself stays
 * thin so each concern is independently reviewable.
 */
import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CCPRegisterTab from "@/components/haccp/CCPRegisterTab";
import CorrectiveActionsTab from "@/components/haccp/CorrectiveActionsTab";

const EMPTY_CCP = {
  step: "", hazard: "", hazard_type: "biological",
  critical_limit: "", monitoring: "", corrective_action: "",
  verification: "", record: "",
};
const EMPTY_CA = {
  ccp_id: "", issue: "", action_taken: "",
  root_cause: "", preventive_measure: "",
};

export default function HACCP() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role !== "auditor";

  const [ccps, setCcps] = useState([]);
  const [cas, setCas] = useState([]);
  const [ccp, setCcp] = useState(EMPTY_CCP);
  const [ca, setCa] = useState(EMPTY_CA);

  const load = async () => {
    if (!activeSite) return;
    const [c, a] = await Promise.all([
      api.get("/haccp/ccps", withSite(activeSite.id)),
      api.get("/haccp/corrective-actions", withSite(activeSite.id)),
    ]);
    setCcps(c.data);
    setCas(a.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite]);

  const submitCcp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/haccp/ccps", ccp, withSite(activeSite.id));
      toast.success("CCP added");
      setCcp(EMPTY_CCP);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const submitCa = async (e) => {
    e.preventDefault();
    try {
      await api.post("/haccp/corrective-actions", ca, withSite(activeSite.id));
      toast.success("Corrective action logged");
      setCa(EMPTY_CA);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const closeCa = async (id) => {
    try {
      await api.patch(`/haccp/corrective-actions/${id}`, { closed: true });
      toast.success("Closed");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const openCount = cas.filter((c) => !c.closed).length;

  return (
    <div className="animate-fade-in" data-testid="haccp-page">
      <PageHeader
        title="HACCP Plan & Corrective Actions"
        subtitle="Your critical control points (CCPs) with limits, monitoring and verification. Log any non-conformance and close it out once resolved."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="corrective_actions" label="CSV" />
            <PdfButton kind="corrective_actions" label="Audit PDF" />
          </div>
        }
      />

      <Tabs defaultValue="ccps">
        <TabsList className="mb-6">
          <TabsTrigger data-testid="tab-ccps" value="ccps">CCP Register</TabsTrigger>
          <TabsTrigger data-testid="tab-cas" value="cas">
            Corrective Actions
            {openCount > 0 && (
              <Badge className="ml-2 bg-ochre-600 text-white hover:bg-ochre-600">
                {openCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ccps">
          <CCPRegisterTab
            ccps={ccps}
            ccp={ccp}
            setCcp={setCcp}
            isAdmin={isAdmin}
            onSubmit={submitCcp}
          />
        </TabsContent>

        <TabsContent value="cas">
          <CorrectiveActionsTab
            cas={cas}
            ccps={ccps}
            ca={ca}
            setCa={setCa}
            canEdit={canEdit}
            onSubmit={submitCa}
            onClose={closeCa}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
